<template>
	<div class="email-templates">
		<div class="preview-shell">
			<section class="template-panel">
				<h2>{{ $t("emailTemplatesPage.templatesTitle") }}</h2>

				<div v-if="loading" class="state">{{ $t("emailTemplatesPage.loading") }}</div>
				<div v-else-if="error" class="state error">{{ error }}</div>
				<div v-else class="template-list">
					<button
						v-for="template in templates"
						:key="template.id"
						type="button"
						class="template-option"
						:class="{ selected: template.id === selectedTemplateId }"
						@click="selectTemplate(template)"
					>
						<span class="name">{{ template.name }}</span>
						<span class="description">{{ template.description }}</span>
						<span class="metadata">{{ template.fields.length }} {{ $t("emailTemplatesPage.fieldsLabel") }}</span>
					</button>
				</div>
			</section>

			<section class="preview-panel">
				<header class="panel-header">
					<div>
						<h2>{{ selectedTemplate?.name ?? $t("emailTemplatesPage.previewTitle") }}</h2>
						<p v-if="selectedTemplate">{{ selectedTemplate.description }}</p>
					</div>
				</header>

				<form v-if="selectedTemplate" class="template-form" @submit.prevent="handlePreview">
					<div
						v-for="field in selectedTemplate.fields"
						:key="field.name"
						class="field"
						:class="{ wide: field.inputType === 'textarea' || field.inputType === 'json' }"
					>
						<span class="label">
							{{ field.label }}
							<span v-if="field.required" class="required">*</span>
						</span>

						<textarea
							v-if="field.inputType === 'textarea' || field.inputType === 'json'"
							v-model="formValues[field.name]"
							:rows="field.inputType === 'json' ? 10 : 4"
							:spellcheck="field.inputType !== 'json'"
						/>
						<label v-else-if="field.inputType === 'boolean'" class="checkbox-field">
							<input v-model="formValues[field.name]" type="checkbox" />
							<span>{{ $t("emailTemplatesPage.enabledLabel") }}</span>
						</label>
						<div v-else-if="field.inputType === 'radio'" class="radio-group">
							<label v-for="option in field.options ?? []" :key="option.value" class="radio-option">
								<input v-model="formValues[field.name]" type="radio" :name="field.name" :value="option.value" />
								<span>{{ option.label }}</span>
							</label>
						</div>
						<input v-else v-model="formValues[field.name]" :type="inputTypeFor(field.inputType)" />

						<span v-if="field.helpText" class="help">{{ field.helpText }}</span>
					</div>

					<div class="form-actions">
						<Button shape="pill" variant="primary" size="m" type="submit" :disabled="previewLoading">
							{{ previewLoading ? $t("emailTemplatesPage.previewLoading") : $t("emailTemplatesPage.previewAction") }}
						</Button>
					</div>
				</form>

				<div v-if="previewError" class="state error">{{ previewError }}</div>

				<section v-if="renderedPreview" class="rendered-preview">
					<div class="summary">
						<div>
							<span class="label">{{ $t("emailTemplatesPage.subjectLabel") }}</span>
							<p>{{ renderedPreview.subject }}</p>
						</div>
						<div v-if="renderedPreview.previewText">
							<span class="label">{{ $t("emailTemplatesPage.previewTextLabel") }}</span>
							<p>{{ renderedPreview.previewText }}</p>
						</div>
					</div>

					<div class="preview-tabs" role="tablist" :aria-label="$t('emailTemplatesPage.previewTitle')">
						<button
							type="button"
							class="tab"
							:class="{ active: activePreviewMode === 'html' }"
							:aria-selected="activePreviewMode === 'html'"
							role="tab"
							@click="activePreviewMode = 'html'"
						>
							{{ $t("emailTemplatesPage.htmlTab") }}
						</button>
						<button
							type="button"
							class="tab"
							:class="{ active: activePreviewMode === 'text' }"
							:aria-selected="activePreviewMode === 'text'"
							role="tab"
							@click="activePreviewMode = 'text'"
						>
							{{ $t("emailTemplatesPage.textTab") }}
						</button>
					</div>

					<iframe
						v-if="activePreviewMode === 'html'"
						class="html-preview"
						:title="$t('emailTemplatesPage.htmlTab')"
						:srcdoc="renderedPreview.html"
						sandbox=""
					/>
					<pre v-else class="text-preview">{{ renderedPreview.text || $t("emailTemplatesPage.noTextPreview") }}</pre>
				</section>
			</section>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useRouter } from "vue-router"
import Button from "@/components/ui/Button.vue"
import { Permission, UseRootStore } from "@/model/RootStore"
import {
	getEmailTemplates,
	previewEmailTemplate,
	type EmailTemplatePreviewFieldInputType,
	type EmailTemplatePreviewField,
	type EmailTemplatePreviewTemplate,
	type RenderedEmailTemplatePreview,
} from "@/utils/ApiClient"

const router = useRouter()
const store = UseRootStore()

const loading = ref(false)
const error = ref("")
const templates = ref<EmailTemplatePreviewTemplate[]>([])
const selectedTemplateId = ref<string | null>(null)
const formValues = ref<Record<string, string | boolean>>({})
const previewLoading = ref(false)
const previewError = ref("")
const renderedPreview = ref<RenderedEmailTemplatePreview | null>(null)
const activePreviewMode = ref<"html" | "text">("html")

const selectedTemplate = computed(
	() => templates.value.find(template => template.id === selectedTemplateId.value) ?? null
)

function inputTypeFor(inputType: EmailTemplatePreviewFieldInputType): string {
	if (inputType === "email" || inputType === "url" || inputType === "number") {
		return inputType
	}

	return "text"
}

function formValueFor(defaultValue: unknown): string | boolean {
	if (typeof defaultValue === "boolean") return defaultValue
	if (defaultValue == null) return ""
	if (typeof defaultValue === "object") return JSON.stringify(defaultValue, null, 2)

	return String(defaultValue)
}

function seedFormValues(template: EmailTemplatePreviewTemplate): void {
	formValues.value = Object.fromEntries(
		template.fields.map(field => [field.name, formValueFor(field.defaultValue)])
	) as Record<string, string | boolean>
}

function selectTemplate(template: EmailTemplatePreviewTemplate): void {
	selectedTemplateId.value = template.id
	seedFormValues(template)
	previewError.value = ""
	renderedPreview.value = null
	activePreviewMode.value = "html"
}

function previewValueFor(field: EmailTemplatePreviewField): unknown {
	const value = formValues.value[field.name]

	if (field.inputType === "boolean") {
		return value === true
	}

	if (field.inputType === "json") {
		if (typeof value !== "string" || value.trim() === "") return null

		try {
			return JSON.parse(value)
		} catch (_error) {
			throw new Error(`${field.label} must be valid JSON`)
		}
	}

	if (field.inputType === "number") {
		if (value === "") return ""

		const numberValue = Number(value)
		if (!Number.isFinite(numberValue)) {
			throw new Error(`${field.label} must be a number`)
		}

		return numberValue
	}

	return typeof value === "string" ? value : String(value ?? "")
}

function previewValues(): Record<string, unknown> {
	if (!selectedTemplate.value) return {}

	return Object.fromEntries(selectedTemplate.value.fields.map(field => [field.name, previewValueFor(field)]))
}

async function handlePreview(): Promise<void> {
	if (!selectedTemplate.value) return

	previewLoading.value = true
	previewError.value = ""
	renderedPreview.value = null

	try {
		renderedPreview.value = await previewEmailTemplate(store.userApiToken, {
			template: selectedTemplate.value.id,
			values: previewValues(),
		})
		activePreviewMode.value = "html"
	} catch (err) {
		previewError.value = err instanceof Error ? err.message : "Failed to preview email template"
	} finally {
		previewLoading.value = false
	}
}

async function loadTemplates(): Promise<void> {
	loading.value = true
	error.value = ""

	try {
		templates.value = await getEmailTemplates(store.userApiToken)
		if (templates.value.length > 0) {
			selectTemplate(templates.value[0])
		}
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Failed to load email templates"
	} finally {
		loading.value = false
	}
}

onMounted(async () => {
	if (!store.currentUser?.has(Permission.EmailCreate)) {
		router.push("/")
		return
	}

	await loadTemplates()
})
</script>

<style scoped>
.preview-shell {
	display: grid;
	grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
	gap: var(--spacing-lg);
	align-items: start;
}

.template-panel,
.preview-panel {
	min-height: 360px;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	background: var(--neutral-0);
	padding: var(--spacing-lg);

	h2 {
		margin: 0;
		color: var(--neutral-900);
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
	}
}

.panel-header {
	margin-bottom: var(--spacing-lg);

	h2 {
		margin-bottom: var(--spacing-xxs);
	}

	p {
		margin: 0;
		color: var(--neutral-600);
		font-size: var(--font-size-small);
	}
}

.state {
	margin-top: var(--spacing-md);
	color: var(--neutral-600);
	font-size: var(--font-size-base);

	&.error {
		color: var(--accent-warning);
	}
}

.template-list {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	margin-top: var(--spacing-md);
}

.template-option {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xxs);
	width: 100%;
	padding: var(--spacing-md);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);
	color: inherit;
	text-align: left;
	cursor: pointer;

	.name {
		color: var(--neutral-900);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
	}

	.description,
	.metadata {
		color: var(--neutral-600);
		font-size: var(--font-size-small);
	}

	&:hover,
	&.selected {
		border-color: var(--primary-green);
	}

	&.selected {
		box-shadow: inset 3px 0 0 var(--primary-green);
	}
}

.template-form {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: var(--spacing-lg);
}

.form-actions {
	display: flex;
	grid-column: 1 / -1;
	justify-content: flex-end;
}

.field {
	display: grid;
	gap: var(--spacing-xs);
	min-width: 0;

	&.wide {
		grid-column: 1 / -1;
	}

	.label {
		color: var(--neutral-900);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-bold);
	}

	.required {
		color: var(--accent-warning);
	}

	input:not([type="checkbox"]):not([type="radio"]),
	textarea {
		display: block;
		width: 100%;
		min-height: 40px;
		border: var(--border-width-thin) solid var(--neutral-400);
		border-radius: var(--border-radius-md);
		background: var(--neutral-50);
		color: var(--neutral-900);
		font: inherit;
		padding: var(--spacing-sm) var(--spacing-md);
		box-shadow: inset 0 1px 0 color-mix(in srgb, var(--neutral-900) 5%, transparent);
		transition:
			background 0.15s ease,
			border-color 0.15s ease,
			box-shadow 0.15s ease;

		&:hover {
			border-color: var(--neutral-600);
		}

		&:focus {
			outline: none;
			border-color: var(--primary-green);
			background: var(--neutral-0);
			box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-green) 18%, transparent);
		}
	}

	textarea {
		resize: vertical;
		line-height: var(--line-height-relaxed);
	}

	.help {
		color: var(--neutral-500);
		font-size: var(--font-size-xs);
	}
}

.checkbox-field {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing-sm);
	color: var(--neutral-800);
	font-size: var(--font-size-base);
}

.radio-group {
	display: flex;
	flex-wrap: wrap;
	gap: var(--spacing-sm);
}

.radio-option {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing-xs);
	min-height: 40px;
	padding: var(--spacing-sm) var(--spacing-md);
	border: var(--border-width-thin) solid var(--neutral-400);
	border-radius: var(--border-radius-md);
	background: var(--neutral-50);
	color: var(--neutral-900);
	font-size: var(--font-size-base);
	cursor: pointer;
	transition:
		background 0.15s ease,
		border-color 0.15s ease,
		box-shadow 0.15s ease;

	&:hover {
		border-color: var(--neutral-600);
	}

	&:has(input:checked) {
		border-color: var(--primary-green);
		background: color-mix(in srgb, var(--primary-green) 10%, var(--neutral-0));
		box-shadow: inset 0 0 0 1px var(--primary-green);
	}

	input {
		accent-color: var(--primary-green);
	}
}

.rendered-preview {
	margin-top: var(--spacing-xl);
	padding-top: var(--spacing-lg);
	border-top: var(--border-width-thin) solid var(--neutral-200);
}

.summary {
	display: grid;
	gap: var(--spacing-md);
	margin-bottom: var(--spacing-lg);

	.label {
		display: block;
		margin-bottom: var(--spacing-xxs);
		color: var(--neutral-600);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
	}

	p {
		margin: 0;
		color: var(--neutral-900);
		font-size: var(--font-size-base);
	}
}

.preview-tabs {
	display: inline-flex;
	gap: var(--spacing-xxs);
	margin-bottom: var(--spacing-md);
	padding: var(--spacing-xxs);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-100);
}

.tab {
	min-width: 72px;
	padding: var(--spacing-xs) var(--spacing-md);
	border: none;
	border-radius: var(--border-radius-sm);
	background: transparent;
	color: var(--neutral-700);
	font: inherit;
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-medium);
	cursor: pointer;

	&.active {
		background: var(--neutral-0);
		color: var(--neutral-900);
		box-shadow: var(--shadow-sm);
	}
}

.html-preview {
	display: block;
	width: 100%;
	min-height: 680px;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-100);
}

.text-preview {
	min-height: 320px;
	margin: 0;
	padding: var(--spacing-lg);
	overflow: auto;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-100);
	color: var(--neutral-900);
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
	font-size: var(--font-size-small);
	line-height: 1.6;
	white-space: pre-wrap;
}

@media (max-width: 760px) {
	.preview-shell {
		grid-template-columns: 1fr;
	}

	.template-form {
		grid-template-columns: 1fr;
	}
}
</style>
